import { createContext, useCallback, useContext, useRef, useState } from "react";

interface ToastOptions {
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

const ToastContext = createContext<(message: string, options?: ToastOptions) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

const DEFAULT_DURATION_MS = 2500;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = ++idRef.current;
      const item: ToastItem = { id, message };
      if (options?.actionLabel) item.actionLabel = options.actionLabel;
      if (options?.onAction) item.onAction = options.onAction;
      setItems((cur) => [...cur, item]);
      setTimeout(() => dismiss(id), options?.durationMs ?? DEFAULT_DURATION_MS);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 items-center"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm pl-4 pr-2 py-2 shadow-lg"
          >
            <span>{t.message}</span>
            {t.actionLabel && (
              <button
                onClick={() => {
                  t.onAction?.();
                  dismiss(t.id);
                }}
                className="rounded bg-slate-700 hover:bg-slate-600 text-slate-100 font-medium px-2 py-1 text-xs"
              >
                {t.actionLabel}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
